import LoadingBar from "@components/ui/LoadingBar";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import Icon1 from "@assets/icons/Icon";
import { Text1, Text2 } from "@components/ui/texts/Texts";
import { useEffect, useMemo, useState } from "react";

import DataGrid from "@components/ui/tables/DataGrid";
import { transformDossiersToTable } from "@components/ui/tables/transformToTables";
import { useDataStore } from "@store/user_org";
import { useDossierStore } from "@store/dossierStore";
import { isOwnerOrAdmin, useSimulateMemberView } from "@utils/permissions";
import { dossiersApi } from "@services/api/dossiers";
import { ordresApi } from "@services/api/ordres";
import ConfirmationModal from "@components/support/ConfirmationModal";
import CheckBoxInput from "@components/ui/selection/CheckBoxInput";
import { showNotification } from "context/notification.service";
import { navigateTo } from "context/navigation.service";
import { searchDossiersFull } from "@services/algolia";
import TagFilterBar from "@components/ui/tags/TagFilterBar";
import { useTagStore } from "@store/tagStore";

const typeTabs = [
  { value: "all", label: "Tous" },
  { value: "particulier", label: "Particuliers" },
  { value: "professionnel", label: "Professionnels" },
  { value: "loueur", label: "Loueurs" },
  { value: "garantie", label: "Garantie" },
  { value: "occasion", label: "Véhicules occasion" },
  { value: "interne", label: "Véhicules internes" },
];

export default function DossiersScreen() {
  const orgData = useDataStore((state) => state.orgData);
  const userData = useDataStore((state) => state.userData);
  useSimulateMemberView();
  const canDelete = isOwnerOrAdmin(userData);
  const {
    dossiers,
    isLoading,
    isRefreshing,
    hasMore,
    hasPrev,
    totalCount,
    page,
    fetchDossiers,
    fetchNextPage,
    fetchPrevPage,
    goToPage,
    setSort,
    removeDossiers,
  } = useDossierStore();

  const [table, setTable] = useState<any>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteLinkedOrdres, setDeleteLinkedOrdres] = useState(false);
  const [activeTab, setActiveTab] = useState<string>("all");
  const [tagFilter, setTagFilter] = useState<string[]>([]);

  const handleTagToggle = (tagId: string) => {
    setTagFilter((prev) =>
      prev.includes(tagId) ? prev.filter((id) => id !== tagId) : [...prev, tagId]
    );
  };

  useEffect(() => {
    if (orgData?.id) {
      useTagStore.getState().fetchTags(orgData.id);
      fetchDossiers(orgData.id, activeTab);
    }
  }, [orgData?.id, activeTab]);

  const filteredDossiers = useMemo(() => {
    if (tagFilter.length === 0) return dossiers;
    return dossiers.filter((d) =>
      (d.tag_ids || []).some((id) => tagFilter.includes(id))
    );
  }, [dossiers, tagFilter]);

  const handleTagsChange = async (dossierId: string, tagIds: string[]) => {
    try {
      const updated = await dossiersApi.update(dossierId, { tag_ids: tagIds });
      useDossierStore.getState().updateDossier(updated);
    } catch {
      showNotification({ message: "Erreur lors de la mise a jour des tags", type: "error" });
    }
  };

  useEffect(() => {
    const table = transformDossiersToTable(filteredDossiers, "all", handleTagsChange);
    setTable(table);
  }, [filteredDossiers]);

  const handleDeleteSelected = async () => {
    if (deleteLinkedOrdres && orgData?.id) {
      try {
        for (const dossierId of selectedIds) {
          const { items: linkedOrdres } = await ordresApi.list({ org_id: orgData.id, dossier_id: dossierId });
          if (linkedOrdres.length > 0) {
            const ordreIds = linkedOrdres.map((o: any) => o.id);
            for (const ordreId of ordreIds) {
              await ordresApi.delete(ordreId);
            }
          }
        }
      } catch {
        showNotification({ type: "error", message: "Erreur lors de la suppression des ordres associés" });
      }
    }
    removeDossiers(selectedIds);
    showNotification({ type: "success", message: `${selectedIds.length} dossier(s) supprimé(s)` });
    setSelectedIds([]);
    setShowDeleteConfirm(false);
    setDeleteLinkedOrdres(false);
  };

  return (
    <div className="h-screen flex flex-col overflow-hidden">
      {showDeleteConfirm && (
        <ConfirmationModal
          title={`Supprimer ${selectedIds.length} dossier(s) ?`}
          message="Cette action est irréversible. Les dossiers et toutes leurs données seront définitivement supprimés."
          onClose={() => { setShowDeleteConfirm(false); setDeleteLinkedOrdres(false); }}
          onConfirm={handleDeleteSelected}
          confirmText="CONFIRMER"
          extraContent={
            <div className="mt-2">
              <CheckBoxInput
                checked={deleteLinkedOrdres}
                onChange={(e: any) => setDeleteLinkedOrdres(e.target.checked)}
                label="Supprimer aussi les ordres associés"
              />
            </div>
          }
        />
      )}

      {/* Header */}
      <div className="flex-shrink-0 pt-10 px-4 lg:px-10 pb-4">
        <div className="flex flex-col md:flex-row w-full max-w-1p2k justify-between">
          <div className="flex flex-col w-full md:w-400">
            <Text1>Dossiers</Text1>
            <Text2>Retrouvez tous vos dossiers ici.</Text2>
          </div>
          <div className="flex flex-row gap-2 items-center">
            {canDelete && selectedIds.length > 0 && (
              <SecondaryButton
                startIcon={<Icon1 name="delete" size={18} />}
                onClick={() => setShowDeleteConfirm(true)}
              >
                {`Supprimer la sélection (${selectedIds.length})`}
              </SecondaryButton>
            )}
          </div>
        </div>
      </div>

      {/* DataGrid area */}
      <div className="flex-1 min-h-0 px-4 lg:px-10 pb-6 flex flex-col gap-3 w-full">
        {(isLoading || isRefreshing) && <LoadingBar />}
        <div className="flex-1 min-h-0 max-w-1p2k w-full">
          {table && (
            <DataGrid
              storageKey="dossiers"
              serverSideSorted
              selectable={canDelete}
              table={table}
              hide_lines={true}
              tabs={typeTabs}
              activeTab={activeTab}
              onTabChange={(tab) => setActiveTab(tab)}
              onSearch={async (query) => {
                if (!orgData?.id) return [];
                return searchDossiersFull(query, orgData.id);
              }}
              onSearchSelect={(option) => navigateTo({ route: "/dossier/" + option.value })}
              onClick={(item) => navigateTo({ route: "/dossier/" + item.id })}
              handleSelectionModelChange={(ids) => setSelectedIds(ids)}
              hasNext={hasMore}
              hasPrev={hasPrev}
              onNext={() => orgData?.id && fetchNextPage(orgData.id)}
              onPrev={() => orgData?.id && fetchPrevPage(orgData.id)}
              totalRows={totalCount}
              serverPage={page}
              totalServerPages={Math.ceil(totalCount / 25)}
              onServerPageChange={(p) => orgData?.id && goToPage(orgData.id, p)}
              handleSortChange={(model) => {
                if (orgData?.id && model.length > 0)
                  setSort(orgData.id, model[0].field, model[0].sort);
              }}
              extraSearchContent={
                <TagFilterBar scope="dossier" selectedTagIds={tagFilter} onToggle={handleTagToggle} />
              }
            />
          )}
        </div>
      </div>
    </div>
  );
}
